import React, { useContext, useMemo } from "react";
import { Box, Checkbox, Skeleton } from "@mui/material";
import ReleaseFlowContext from "../../context/releaseFlowContext";
import { getAttachmentSelectionKey } from "../../utils/getAttachmentSelectionKey";
import { convertDatePtBr } from "../../utils/convertDatePtBr";
import { ReleaseFlowVersionLabel } from "./ReleaseFlowVersionLabel/ReleaseFlowVersionLabel";

export const releaseFlowCheckboxSx = {
  color: "#7F808E",
  "&.Mui-checked": { color: "#36345a" },
  "&.MuiCheckbox-indeterminate": { color: "#36345a" },
};

export const releaseFlowGridTemplateColumns =
  "minmax(320px, 2fr) 1fr 1fr 90px 90px 110px";

export const releaseFlowGridTemplateColumnsNoProduction =
  "minmax(320px, 2fr) 1fr 1fr 90px 110px";

export const releaseFlowGridTemplateColumnsNoProductionNoHub =
  "minmax(320px, 2fr) 1fr 1fr 90px";

// Same as the templates above, but with an extra fixed-width track for the
// "Bloco X" label shown on chapter attachments (materials/addenda leave it
// blank) - kept local/unexported since `ReleaseFlowStepVersions.js` (step 2)
// reuses the plain templates above and doesn't render a block column.
const releaseFlowGridTemplateColumnsWithBlock =
  "minmax(280px, 2fr) 80px 1fr 1fr 90px 90px 110px";

const releaseFlowGridTemplateColumnsWithBlockNoProduction =
  "minmax(280px, 2fr) 80px 1fr 1fr 90px 110px";

const releaseFlowGridTemplateColumnsWithBlockNoProductionNoHub =
  "minmax(280px, 2fr) 80px 1fr 1fr 90px";

export const releaseFlowHeadingSx = {
  fontSize: "13px",
  fontWeight: "bold",
  color: "#43444D",
  textTransform: "uppercase",
  letterSpacing: "0.02em",
  marginBottom: "16px",
};

export const releaseFlowInlineHeadingSx = {
  fontSize: "13px",
  fontWeight: "bold",
  color: "#43444D",
  textTransform: "uppercase",
  letterSpacing: "0.02em",
  gridColumn: "1 / 4",
};

export const releaseFlowTableSx = {
  headerRow: {
    display: "grid",
    gridTemplateColumns: releaseFlowGridTemplateColumns,
    columnGap: "16px",
    alignItems: "center",
    padding: "0 16px 2px 0",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: "6px",
    overflow: "hidden",
  },
  row: {
    display: "grid",
    gridTemplateColumns: releaseFlowGridTemplateColumns,
    columnGap: "16px",
    alignItems: "center",
    padding: "4px 16px",
    fontSize: "14px",
    color: "#43444D",
    borderBottom: "1px solid #E2E4FF",
    "&:last-of-type": { borderBottom: "none" },
  },
};

export const releaseFlowColumnHeaderLabelSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: "4px",
  fontSize: "13px",
  fontWeight: "bold",
  color: "#43444D",
  "& .MuiCheckbox-root": {
    marginRight: "-6px",
  },
};

export const releaseFlowColumnCheckboxWrapperSx = {
  display: "flex",
  justifyContent: "flex-start",
};

export const releaseFlowFileNameSx = {
  paddingRight: "24px",
  minWidth: 0,
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

export const releaseFlowVersionSx = {
  textAlign: "left",
};

// Only populated for chapter attachments (`type === "chapter"`) - materials
// and addenda have no block, so the cell renders empty for those rows.
export const releaseFlowBlockSx = {
  textAlign: "left",
};

export const releaseFlowDateSx = {
  textAlign: "center",
  paddingRight: "24px",
};

// Memoized so toggling one checkbox only re-renders that row: `rowDestinations`
// keeps the same reference for every key except the one that changed (see
// `toggleMapDestination` in releaseFlowReducer.js), and `receivedDate` is a
// primitive string computed by the parent, so unrelated rows' props stay
// referentially/value-equal across renders.
const ReleaseFlowDestinationRow = React.memo(
  ({
    attachment,
    rowDestinations,
    receivedDate,
    toggleDestination,
    gridTemplateColumns,
    showProduction,
    productionLoading,
    showCultureHub,
    cultureHubReleaseLoading,
  }) => {
    const key = getAttachmentSelectionKey(attachment);

    return (
      <Box sx={{ ...releaseFlowTableSx.row, gridTemplateColumns }}>
        <Box sx={releaseFlowFileNameSx}>{attachment.fileName}</Box>
        <Box sx={releaseFlowBlockSx}>
          {attachment.type === "chapter"
            ? `Bloco ${attachment.blockNumber}`
            : null}
        </Box>
        <Box sx={releaseFlowVersionSx}>
          <ReleaseFlowVersionLabel attachment={attachment} />
        </Box>
        <Box sx={releaseFlowDateSx}>{receivedDate}</Box>
        <Box sx={releaseFlowColumnCheckboxWrapperSx}>
          <Checkbox
            checked={!!rowDestinations.channel}
            onChange={() => toggleDestination(key, "channel")}
            size="small"
            sx={releaseFlowCheckboxSx}
          />
        </Box>
        {productionLoading ? (
          <Box sx={releaseFlowColumnCheckboxWrapperSx}>
            <Skeleton variant="rounded" width={20} height={20} animation="wave" />
          </Box>
        ) : (
          showProduction && (
            <Box sx={releaseFlowColumnCheckboxWrapperSx}>
              <Checkbox
                checked={!!rowDestinations.production}
                onChange={() => toggleDestination(key, "production")}
                size="small"
                sx={releaseFlowCheckboxSx}
              />
            </Box>
          )
        )}
        {cultureHubReleaseLoading ? (
          <Box sx={releaseFlowColumnCheckboxWrapperSx}>
            <Skeleton variant="rounded" width={20} height={20} animation="wave" />
          </Box>
        ) : (
          showCultureHub && (
            <Box sx={releaseFlowColumnCheckboxWrapperSx}>
              <Checkbox
                checked={!!rowDestinations.cultureHub}
                onChange={() => toggleDestination(key, "cultureHub")}
                size="small"
                sx={releaseFlowCheckboxSx}
              />
            </Box>
          )
        )}
      </Box>
    );
  }
);

const ReleaseFlowStepDestinations = () => {
  const {
    attachments,
    destinations,
    toggleDestination,
    toggleAllDestinations,
    productionReleaseEnabled: TEXT_RELEASE_FEATURE_FOR_PRODUCTION_ENABLED,
    productionReleaseLoading,
    cultureHubReleaseEnabled: CULTURE_HUB_RELEASE_ENABLED,
    cultureHubReleaseLoading,
  } = useContext(ReleaseFlowContext);

  // Reserve the wider ("with production") layout while the flag is still
  // resolving so the column doesn't shift once for the skeleton and again
  // once the real value lands - it only narrows if production ends up
  // disabled.
  const showProduction =
    TEXT_RELEASE_FEATURE_FOR_PRODUCTION_ENABLED || productionReleaseLoading;
  const showCultureHub =
    CULTURE_HUB_RELEASE_ENABLED || cultureHubReleaseLoading;

  const gridTemplateColumns = showProduction && showCultureHub
    ? releaseFlowGridTemplateColumnsWithBlock
    : showProduction || showCultureHub
    ? releaseFlowGridTemplateColumnsWithBlockNoProduction
    : releaseFlowGridTemplateColumnsWithBlockNoProductionNoHub;

  const {
    allChannelChecked,
    someChannelChecked,
    allCultureHubChecked,
    someCultureHubChecked,
    allProductionChecked,
    someProductionChecked,
  } = useMemo(() => {
    const values = attachments
      .map((attachment) => destinations[getAttachmentSelectionKey(attachment)])
      .filter(Boolean);

    const channelCheckedCount = values.filter((v) => v.channel).length;
    const cultureHubCheckedCount = values.filter((v) => v.cultureHub).length;
    const productionCheckedCount = values.filter((v) => v.production).length;

    return {
      allChannelChecked:
        !!values.length && channelCheckedCount === values.length,
      someChannelChecked:
        channelCheckedCount > 0 && channelCheckedCount < values.length,
      allCultureHubChecked:
        !!values.length && cultureHubCheckedCount === values.length,
      someCultureHubChecked:
        cultureHubCheckedCount > 0 && cultureHubCheckedCount < values.length,
      allProductionChecked:
        !!values.length && productionCheckedCount === values.length,
      someProductionChecked:
        productionCheckedCount > 0 && productionCheckedCount < values.length,
    };
  }, [attachments, destinations]);

  return (
    <Box>
      <Box sx={{ ...releaseFlowTableSx.headerRow, gridTemplateColumns }}>
        <Box sx={{ ...releaseFlowInlineHeadingSx, gridColumn: "1 / 5" }}>
          Marque o local para qual deseja liberar os textos
        </Box>
        <Box sx={releaseFlowColumnHeaderLabelSx}>
          <Checkbox
            checked={allChannelChecked}
            indeterminate={someChannelChecked}
            onChange={(ev) =>
              toggleAllDestinations("channel", ev.target.checked)
            }
            disabled={!attachments.length}
            size="small"
            sx={releaseFlowCheckboxSx}
          />
          <Box component="span">Canal</Box>
        </Box>
        {(TEXT_RELEASE_FEATURE_FOR_PRODUCTION_ENABLED ||
          productionReleaseLoading) && (
          <Box sx={releaseFlowColumnHeaderLabelSx}>
            {productionReleaseLoading ? (
              <Skeleton variant="rounded" width={90} height={24} animation="wave" />
            ) : (
              <>
                <Checkbox
                  checked={allProductionChecked}
                  indeterminate={someProductionChecked}
                  onChange={(ev) =>
                    toggleAllDestinations("production", ev.target.checked)
                  }
                  disabled={!attachments.length}
                  size="small"
                  sx={releaseFlowCheckboxSx}
                />
                <Box component="span">Produção</Box>
              </>
            )}
          </Box>
        )}
        {(CULTURE_HUB_RELEASE_ENABLED || cultureHubReleaseLoading) && (
          <Box sx={releaseFlowColumnHeaderLabelSx}>
            {cultureHubReleaseLoading ? (
              <Skeleton variant="rounded" width={90} height={24} animation="wave" />
            ) : (
              <>
                <Checkbox
                  checked={allCultureHubChecked}
                  indeterminate={someCultureHubChecked}
                  onChange={(ev) =>
                    toggleAllDestinations("cultureHub", ev.target.checked)
                  }
                  disabled={!attachments.length}
                  size="small"
                  sx={releaseFlowCheckboxSx}
                />
                <Box component="span">HUB Cultura</Box>
              </>
            )}
          </Box>
        )}
      </Box>

      <Box sx={releaseFlowTableSx.card}>
        {attachments.map((attachment) => {
          const key = getAttachmentSelectionKey(attachment);
          const rowDestinations = destinations[key] || {};
          const receivedDate = convertDatePtBr(
            attachment?.deliveryControl?.completionDates?.received
          );

          return (
            <ReleaseFlowDestinationRow
              key={key}
              attachment={attachment}
              rowDestinations={rowDestinations}
              receivedDate={receivedDate}
              toggleDestination={toggleDestination}
              gridTemplateColumns={gridTemplateColumns}
              showProduction={TEXT_RELEASE_FEATURE_FOR_PRODUCTION_ENABLED}
              productionLoading={productionReleaseLoading}
              showCultureHub={CULTURE_HUB_RELEASE_ENABLED}
              cultureHubReleaseLoading={cultureHubReleaseLoading}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default ReleaseFlowStepDestinations;
